import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WebSocketProvider } from './context/WebSocketContext';
import './i18n';

// Layout
import MainLayout from './components/layout/MainLayout';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import JobsPage from './pages/JobsPage';
import JobDetailPage from './pages/JobDetailPage';
import ContractorsPage from './pages/ContractorsPage';
import ContractorDetailPage from './pages/ContractorDetailPage';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import MessagesPage from './pages/MessagesPage';
import SubscriptionPage from './pages/SubscriptionPage';
import AdminPage from './pages/AdminPage';
import PostJobPage from './pages/PostJobPage';
import AIMatchingPage from './pages/AIMatchingPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import CreateProjectPage from './pages/CreateProjectPage';
import AnalyticsPage from './pages/AnalyticsPage';
import FavoritesPage from './pages/FavoritesPage';
import InsurancePage from './pages/InsurancePage';
import EditJobPage from './pages/EditJobPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import LegalNoticePage from './pages/LegalNoticePage';
import SuggestionsPage from './pages/SuggestionsPage';
import PublicTendersPage from './pages/PublicTendersPage';
import BuildingZonesPage from './pages/BuildingZonesPage';
import FaqPage from './pages/FaqPage';
import { BlogListPage, BlogPostPage } from './pages/BlogPages';
import LocalTradeCityPage from './pages/LocalTradeCityPage';
import LocalTradeProvincePage from './pages/LocalTradeProvincePage';
import DashboardNewsPage from './pages/DashboardNewsPage';
import { Analytics } from './components/seo/Analytics';

// Auth Callback for Google OAuth
const AuthCallback = () => {
  const navigate = useNavigate();
  const { exchangeSession } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          await exchangeSession(sessionId);
          // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
          navigate('/dashboard', { replace: true });
        } catch (error) {
          console.error('Auth callback error:', error);
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [exchangeSession, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E3A8A] mx-auto"></div>
        <p className="mt-4 text-[#374151]">Connexion en cours...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E3A8A]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Redirect admin from dashboard to admin panel
  if (isAdmin && location.pathname === '/dashboard') {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

// App Router Component
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<MainLayout />}>
        <Route index element={<HomePage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="register" element={<RegisterPage />} />
        <Route path="forgot-password" element={<ForgotPasswordPage />} />
        <Route path="reset-password" element={<ResetPasswordPage />} />
        <Route path="jobs" element={<JobsPage />} />
        <Route path="jobs/:id" element={<JobDetailPage />} />
        <Route path="contractors" element={<ContractorsPage />} />
        <Route path="contractors/:id" element={<ContractorDetailPage />} />
        
        {/* Protected Routes */}
        <Route path="dashboard" element={
          <ProtectedRoute><DashboardPage /></ProtectedRoute>
        } />
        <Route path="dashboard/profile" element={
          <ProtectedRoute><ProfilePage /></ProtectedRoute>
        } />
        <Route path="dashboard/suggestions" element={
          <ProtectedRoute><SuggestionsPage /></ProtectedRoute>
        } />
        <Route path="dashboard/messages" element={
          <ProtectedRoute><MessagesPage /></ProtectedRoute>
        } />
        <Route path="dashboard/messages/:partnerId" element={
          <ProtectedRoute><MessagesPage /></ProtectedRoute>
        } />
        <Route path="dashboard/subscription" element={
          <ProtectedRoute><SubscriptionPage /></ProtectedRoute>
        } />
        <Route path="dashboard/subscription/success" element={
          <ProtectedRoute><SubscriptionPage /></ProtectedRoute>
        } />
        <Route path="dashboard/post-job" element={
          <ProtectedRoute><PostJobPage /></ProtectedRoute>
        } />
        <Route path="dashboard/edit-job/:id" element={
          <ProtectedRoute><EditJobPage /></ProtectedRoute>
        } />
        <Route path="dashboard/jobs/:jobId/matching" element={
          <ProtectedRoute><AIMatchingPage /></ProtectedRoute>
        } />
        <Route path="dashboard/projects" element={
          <ProtectedRoute><ProjectsPage /></ProtectedRoute>
        } />
        <Route path="dashboard/projects/:projectId" element={
          <ProtectedRoute><ProjectDetailPage /></ProtectedRoute>
        } />
        <Route path="dashboard/jobs/:jobId/create-project" element={
          <ProtectedRoute><CreateProjectPage /></ProtectedRoute>
        } />
        <Route path="dashboard/analytics" element={
          <ProtectedRoute><AnalyticsPage /></ProtectedRoute>
        } />
        <Route path="dashboard/favorites" element={
          <ProtectedRoute><FavoritesPage /></ProtectedRoute>
        } />
        <Route path="dashboard/insurance" element={
          <ProtectedRoute><InsurancePage /></ProtectedRoute>
        } />
        
        {/* Admin Routes */}
        <Route path="admin/*" element={
          <ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>
        } />

        {/* Legal Pages */}
        <Route path="terms" element={<TermsPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="mentions-legales" element={<LegalNoticePage />} />
        <Route path="marches-publics" element={<PublicTendersPage />} />
        <Route path="zones-a-construire" element={<BuildingZonesPage />} />
        <Route path="faq" element={<FaqPage />} />
        <Route path="blog" element={<BlogListPage />} />
        <Route path="blog/:slug" element={<BlogPostPage />} />
        <Route path="sous-traitants/:trade/province/:province" element={<LocalTradeProvincePage />} />
        <Route path="sous-traitants/:trade/:city" element={<LocalTradeCityPage />} />
        <Route path="dashboard/actualites" element={<ProtectedRoute><DashboardNewsPage /></ProtectedRoute>} />
      </Route>
    </Routes>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <WebSocketProvider>
            <Analytics />
            <AppRouter />
            <Toaster position="top-right" richColors />
          </WebSocketProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
